import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import UserServices from "../../UserServices/UserServices";
import "../PtDashboard/ptDashboard.css";

const num = (v) => Number(v || 0).toLocaleString("en-PK");

const METRIC_CHIPS = [
  { key: "weight", label: "Weight", unit: "kg", lowerIsBetter: true },
  { key: "body_mass_index", label: "BMI", unit: "", lowerIsBetter: true },
  { key: "fat", label: "Fat", unit: "%", lowerIsBetter: true },
  { key: "waist", label: "Waist", unit: "in", lowerIsBetter: true },
  { key: "chest", label: "Chest", unit: "in", lowerIsBetter: false },
  { key: "rhr", label: "RHR", unit: "bpm", lowerIsBetter: true },
];

const deltaLabel = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!n) return null;
  return `${n > 0 ? "+" : ""}${n}`;
};

const deltaClass = (value, lowerIsBetter) => {
  const n = Number(value || 0);
  if (!n) return "";
  const good = lowerIsBetter ? n < 0 : n > 0;
  return good ? " pt-delta-good" : " pt-delta-bad";
};

const assessmentType = (type) => {
  const label = String(type || "").trim();
  if (!label || label === "N/A" || /pre assessment/i.test(label)) return "Assessment";
  return label;
};

function ClientPicker({ clients, value, onPick, placeholder, inputId }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => String(c.id) === String(value));
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return clients
      .filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          String(c.id).includes(q) ||
          String(c.phone || "").includes(q)
      )
      .slice(0, 25);
  }, [clients, q]);

  return (
    <div className="position-relative">
      <input
        id={inputId}
        type="text"
        className="form-control"
        autoComplete="off"
        placeholder={placeholder}
        value={open ? query : selected ? `${selected.name} (#${selected.id})` : ""}
        onFocus={() => {
          setQuery("");
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 180);
        }}
      />
      {open && q ? (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 30, maxHeight: 240, overflowY: "auto" }}
        >
          {matches.length ? (
            matches.map((c) => (
              <li
                key={c.id}
                className="list-group-item list-group-item-action py-1"
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(c);
                  setOpen(false);
                }}
              >
                {c.name}{" "}
                <span className="text-muted">
                  #{c.id}
                  {c.phone ? ` · ${c.phone}` : ""}
                </span>
              </li>
            ))
          ) : (
            <li className="list-group-item text-muted py-1">No client in your branch</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Branch post-assessments (the same records GT and PT add).
 * Pre-assessment forms are not listed.
 */
export default function ClientAssessmentsPanel({ branchId, inputId = "assess-client" }) {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [assessClientId, setAssessClientId] = useState("");
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const load = useCallback(async () => {
    if (branchId === undefined || branchId === null || branchId === "") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await UserServices.gtDashboardSummary({
        branch_id: branchId || undefined,
        start_date: startDate,
        end_date: endDate,
      });
      setAssessments(res?.data?.data?.assessments || []);
    } catch (err) {
      console.error(err);
      setAssessments([]);
      toast.error(err?.response?.data?.message || "Could not load assessments.");
    } finally {
      setLoading(false);
    }
  }, [branchId, startDate, endDate]);

  const loadClients = useCallback(async () => {
    if (branchId === undefined || branchId === null || branchId === "" || String(branchId) === "0") return;
    try {
      const res = await UserServices.getClientsName(branchId);
      const rows = (res?.data?.data || []).map((c) => ({
        id: c.id,
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        phone: c.phone,
      }));
      setClients(rows);
    } catch (err) {
      console.error(err);
      setClients([]);
    }
  }, [branchId]);

  const loadTimeline = useCallback(async () => {
    if (!assessClientId) {
      setTimeline(null);
      return;
    }
    setTimelineLoading(true);
    try {
      const res = await UserServices.gtClientAssessments({ client_id: assessClientId });
      setTimeline(res?.data?.data || null);
    } catch (err) {
      console.error(err);
      setTimeline(null);
      toast.error(err?.response?.data?.message || "Could not load assessment timeline.");
    } finally {
      setTimelineLoading(false);
    }
  }, [assessClientId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const tlItems = (timeline?.timeline || []).filter(
    (item) => item.kind !== "questionnaire" && !/pre assessment/i.test(item.type || "")
  );
  const tlClient = timeline?.client || null;

  return (
    <div className="pt-panel">
      <div className="pt-panel-head">
        <h5>Client assessments</h5>
        <span className="pt-chip">
          {tlClient
            ? `${num(tlItems.length)} entries for ${tlClient.name}`
            : `${num(assessments.length)} records in range`}
        </span>
      </div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label className="pt-field-label" htmlFor={`${inputId}-from`}>
            From
          </label>
          <input
            id={`${inputId}-from`}
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="pt-field-label" htmlFor={`${inputId}-to`}>
            To
          </label>
          <input
            id={`${inputId}-to`}
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <label className="pt-field-label" htmlFor={inputId}>
            Client
          </label>
          <ClientPicker
            inputId={inputId}
            clients={clients}
            value={assessClientId}
            placeholder="Type a name, ID, or phone from your branch"
            onPick={(c) => setAssessClientId(c ? String(c.id) : "")}
          />
        </div>
      </div>
      <div className="pt-row-actions mb-3">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <Link
          className={`btn btn-sm btn-outline-danger${assessClientId ? "" : " disabled"}`}
          to={assessClientId ? `/postAssessment/${assessClientId}` : "#"}
          onClick={(e) => {
            if (!assessClientId) e.preventDefault();
          }}
        >
          Add Assessment
        </Link>
        <Link
          className={`btn btn-sm btn-outline-dark${assessClientId ? "" : " disabled"}`}
          to={assessClientId ? `/viewAssessmentInfo/${assessClientId}` : "#"}
          onClick={(e) => {
            if (!assessClientId) e.preventDefault();
          }}
        >
          View / Download
        </Link>
      </div>

      {assessClientId ? (
        timelineLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-danger" role="status" />
          </div>
        ) : tlItems.length ? (
          <ol className="pt-timeline">
            {tlItems.map((item) => (
              <li className="pt-tl-item" key={`${item.kind}-${item.id}`}>
                <span className="pt-tl-dot pt-tl-dot-measurement" />
                <div className="pt-tl-card">
                  <div className="pt-tl-head">
                    <div>
                      <strong className="pt-tl-date">
                        {item.date ? moment(item.date).format("DD MMM YYYY") : "No date"}
                      </strong>
                      <span className="pt-tl-type">{assessmentType(item.type)}</span>
                    </div>
                    <Link className="btn btn-sm btn-outline-dark" to={`/viewAssessmentInfo/${assessClientId}`}>
                      View
                    </Link>
                  </div>
                  <p className="pt-tl-meta">
                    Added by <strong>{item.added_by || "Unknown"}</strong>
                    {item.recorded_at
                      ? ` · recorded ${moment(item.recorded_at).format("DD MMM YYYY, hh:mm A")}`
                      : ""}
                  </p>
                  {item.metrics ? (
                    <div className="pt-tl-metrics">
                      {METRIC_CHIPS.filter(
                        (m) =>
                          item.metrics[m.key] !== null &&
                          item.metrics[m.key] !== undefined &&
                          item.metrics[m.key] !== ""
                      ).map((m) => {
                        const change = deltaLabel(item.changes?.[m.key]);
                        return (
                          <span className="pt-tl-metric" key={m.key}>
                            <span className="pt-tl-metric-label">{m.label}</span>
                            <span className="pt-tl-metric-value">
                              {item.metrics[m.key]}
                              {m.unit ? ` ${m.unit}` : ""}
                            </span>
                            {change ? (
                              <span className={`pt-tl-metric-delta${deltaClass(item.changes?.[m.key], m.lowerIsBetter)}`}>
                                {change}
                              </span>
                            ) : null}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="pt-empty">
            No assessments recorded for this client yet. Use “Add Assessment” to create the first one.
          </p>
        )
      ) : loading && !assessments.length ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-danger" role="status" />
        </div>
      ) : assessments.length ? (
        <div className="pt-table-wrap">
          <table className="pt-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Added by</th>
                <th>Weight</th>
                <th>BMI</th>
                <th>Fat %</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((row) => (
                <tr key={row.id}>
                  <td>{row.date ? moment(row.date).format("DD MMM YYYY") : "—"}</td>
                  <td>
                    {row.client_name || `#${row.client_id}`}
                    <div className="small text-muted">{num(row.client_assessment_count)} total</div>
                  </td>
                  <td>{row.added_by || "—"}</td>
                  <td>{row.weight ?? "—"}</td>
                  <td>{row.body_mass_index ?? "—"}</td>
                  <td>{row.fat ?? "—"}</td>
                  <td>
                    <div className="pt-row-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setAssessClientId(String(row.client_id))}
                      >
                        Timeline
                      </button>
                      <Link className="btn btn-sm btn-outline-dark" to={`/viewAssessmentInfo/${row.client_id}`}>
                        View
                      </Link>
                      <Link className="btn btn-sm btn-outline-danger" to={`/postAssessment/${row.client_id}`}>
                        Add
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="pt-empty">
          No assessments in this date range. Pick a client above to see their history, or add one.
        </p>
      )}
    </div>
  );
}
